/**
 * O minimapa do escritório — a parte que não precisa de ecrã.
 *
 * Tudo o que aqui está é puro (entra o mapa de `space.ts`, sai uma cor, um
 * ponto, uma lista, ou traços num contexto de canvas que se passa por
 * argumento), para se poder testar sem browser. O desenho a 30fps vive em
 * `OfficeMinimap.tsx`.
 */
import {
  COLS,
  DOORS,
  ESCADAS,
  EXTERIOR_DOORS,
  type Facing,
  type FloorKind,
  floorAt,
  inLake,
  isOutside,
  isUnderground,
  isUpstairs,
  isWalkable,
  isWall,
  MAP_HEIGHT,
  onGardenPath,
  PROP_SPECS,
  PROPS,
  type PropKind,
  ROOMS,
  ROWS,
  type Room,
  roomAt,
  type SpaceOccupant,
  SURFACE_ROWS,
  TELEPORTS,
  TILE,
  TREES,
  type TreeSize,
  teleportCenter,
  trunkTile,
  UPSTAIRS_OFFSET_ROWS,
  UPSTAIRS_TOP_ROW,
} from "@greenvoice/shared";

import { eTelhado, MEDIDA_DA_PECA, PECAS_DO_TELHADO } from "./officeRoof";

/** A paleta do mapa: tons baixos, para as pessoas e os ícones saltarem à vista. */
export const COR = {
  parede: "#1b2026",
  vedacao: "#26301f",
  arvore: "#24421f",
  copa: "#2d5a27",
  copaLuz: "#3b7431",
  relva: "#3d6632",
  relvaTufo: "#355a2c",
  agua: "#2d6a96",
  caminho: "#9c8960",
  porta: "#d8c38c",
  corredor: "#5b636e",
  rocha: "#101316",
  telhado: "#6b7075",
  painel: "#223d63",
  maquina: "#b3bac1",
  heliponto: "#2d4a3b",
} as const;

export const COR_DO_CHAO: Record<FloorKind, string> = {
  wood: "#7a5a3a",
  carpet: "#3d4b61",
  lobby: "#5b636e",
  lounge: "#5a4868",
  tile: "#6d7a82",
  asphalt: "#393c41",
  concrete: "#53575c",
};

const chave = (col: number, row: number) => `${col},${row}`;
const PORTAS = new Set([...DOORS, ...EXTERIOR_DOORS].map(([col, row]) => chave(col, row)));
const TRONCOS = new Set(
  TREES.map((arvore) => {
    const { col, row } = trunkTile(arvore);
    return chave(col, row);
  }),
);

/**
 * A cor de base de um tile — o chão, a parede, a relva, a água.
 *
 * A mobília sólida também é "parede" para a colisão, mas aqui dá-se o CHÃO por
 * baixo dela: os móveis desenham-se a seguir, cada um com a sua forma, e uma
 * secretária pintada como parede tapava a sala.
 */
export function corDoTile(col: number, row: number): string {
  // No 1.º andar, fora das salas: o telhado, a parede do bloco, ou — por cima do
  // jardim — o próprio jardim de baixo, que é o que se vê de lá.
  if (isUpstairs(row) && floorAt(col, row) == null) {
    if (eTelhado(col, row)) return COR.telhado;
    const baixo = row - UPSTAIRS_OFFSET_ROWS;
    return isOutside(col, baixo) ? corDoTile(col, baixo) : COR.parede;
  }
  if (PORTAS.has(chave(col, row))) return COR.porta;
  if (inLake(col, row)) return COR.agua;
  const chao = floorAt(col, row);
  if (isWall(col, row)) {
    if (chao) return COR_DO_CHAO[chao];
    if (TRONCOS.has(chave(col, row))) return COR.relva; // a copa pinta-se por cima
    return isOutside(col, row) ? COR.vedacao : COR.parede;
  }
  if (chao) return COR_DO_CHAO[chao];
  if (onGardenPath(col, row)) return COR.caminho;
  if (isOutside(col, row)) return COR.relva;
  if (isUnderground(row)) return COR.rocha;
  return COR.corredor;
}

// ── Móveis ─────────────────────────────────────────────────────────────────

/** Como um móvel aparece no mapa, visto de cima. */
export interface EstiloDeMovel {
  cor: string;
  /**
   * `caixa` = rectângulo · `redondo` = círculo · `tapete` = rectângulo
   * translúcido · `vedacao` = barra fina ao comprido · `baias` = linhas de
   * estacionamento · `escada` = só os degraus (a pegada leva corrimão e remate).
   */
  forma: "caixa" | "redondo" | "tapete" | "vedacao" | "baias" | "escada";
  /** Folga dentro da pegada, em fracção de tile (0 = enche o tile). */
  margem?: number;
  contorno?: string;
}

const MADEIRA_ESCURA = "#3a2a1c";
const CADEIRA = "#1c2026";

/**
 * O estilo de cada móvel. Uma entrada por kind que exista no mapa — há um teste
 * a exigi-lo, para um móvel novo nunca ficar invisível no minimapa sem ninguém
 * decidir que é assim.
 */
export const ESTILO_DOS_MOVEIS: Partial<Record<PropKind, EstiloDeMovel>> = {
  // Escritório
  salesDesk: { cor: MADEIRA_ESCURA, forma: "caixa", margem: 0.1, contorno: "#1a130c" },
  deskPC: { cor: MADEIRA_ESCURA, forma: "caixa", margem: 0.1, contorno: "#1a130c" },
  deskDual: { cor: "#22303c", forma: "caixa", margem: 0.1, contorno: "#38bdf8" },
  receptionDesk: { cor: "#2f3a2f", forma: "caixa", margem: 0.08, contorno: "#4ade80" },
  officeChair: { cor: CADEIRA, forma: "redondo", margem: 0.28 },
  meetingChair: { cor: "#4d7c0f", forma: "redondo", margem: 0.3 },
  diningChair: { cor: "#5b3b22", forma: "redondo", margem: 0.32 },
  meetingTable: { cor: "#5b3b22", forma: "caixa", margem: 0.08, contorno: "#2a1a0e" },
  diningTable: { cor: "#6b4526", forma: "caixa", margem: 0.12, contorno: "#2a1a0e" },
  coffeeTable: { cor: "#5b3b22", forma: "caixa", margem: 0.18 },
  gameTable: { cor: "#1e3a8a", forma: "caixa", margem: 0.12, contorno: "#facc15" },
  couch: { cor: "#4a5f86", forma: "caixa", margem: 0.1, contorno: "#2c3a55" },
  couchSide: { cor: "#4a5f86", forma: "caixa", margem: 0.1, contorno: "#2c3a55" },
  rug: { cor: "#3c8d5f", forma: "tapete", margem: 0.04 },
  bookshelf: { cor: "#4a3320", forma: "caixa", margem: 0.12 },
  whiteboard: { cor: "#e5e7eb", forma: "caixa", margem: 0.32 },
  tv: { cor: "#0f1115", forma: "caixa", margem: 0.3 },
  analyticsScreen: { cor: "#0e7490", forma: "caixa", margem: 0.3 },
  counter: { cor: "#9ca3af", forma: "caixa", margem: 0.1, contorno: "#4b5563" },
  fridge: { cor: "#d7dde3", forma: "caixa", margem: 0.14 },
  waterCooler: { cor: "#bae6fd", forma: "redondo", margem: 0.3 },
  plant: { cor: "#3f8f3a", forma: "redondo", margem: 0.2 },
  plantTall: { cor: "#2f7a2e", forma: "redondo", margem: 0.14 },
  flowerBox: { cor: "#d9467a", forma: "caixa", margem: 0.22, contorno: "#3f8f3a" },
  grandfatherClock: { cor: "#6b3f1f", forma: "caixa", margem: 0.26, contorno: "#facc15" },
  arcade: { cor: "#7c3aed", forma: "caixa", margem: 0.18 },
  // Bunker
  serverRack: { cor: "#0f172a", forma: "caixa", margem: 0.1, contorno: "#22d3ee" },
  aiConsole: { cor: "#134e4a", forma: "caixa", margem: 0.08, contorno: "#2dd4bf" },
  bunkerHatch: { cor: "#6b7280", forma: "caixa", margem: 0.1, contorno: "#f59e0b" },
  // Exterior
  floatingHouse: { cor: "#b88a5a", forma: "caixa", margem: 0.05, contorno: "#6b4a2b" },
  mobileHome: { cor: "#d8cbb0", forma: "caixa", margem: 0.05, contorno: "#8b7d62" },
  mobileHomeFlip: { cor: "#d8cbb0", forma: "caixa", margem: 0.05, contorno: "#8b7d62" },
  mobileHomeArch: { cor: "#cdbf9f", forma: "caixa", margem: 0.05, contorno: "#8b7d62" },
  mobileHomeArchFlip: { cor: "#cdbf9f", forma: "caixa", margem: 0.05, contorno: "#8b7d62" },
  mobileHomeSide: { cor: "#e2d7bf", forma: "caixa", margem: 0.05, contorno: "#8b7d62" },
  mobileHomeSideFlip: { cor: "#e2d7bf", forma: "caixa", margem: 0.05, contorno: "#8b7d62" },
  homeDome: { cor: "#e5e7eb", forma: "redondo", margem: 0.05, contorno: "#9ca3af" },
  homeCapsule: { cor: "#cbd5e1", forma: "caixa", margem: 0.08, contorno: "#64748b" },
  homeCapsuleClosed: { cor: "#cbd5e1", forma: "caixa", margem: 0.08, contorno: "#64748b" },
  homeModular: { cor: "#a8b3bd", forma: "caixa", margem: 0.06, contorno: "#475569" },
  carSedan: { cor: "#2563eb", forma: "caixa", margem: 0.2, contorno: "#0b0f13" },
  carSport: { cor: "#dc2626", forma: "caixa", margem: 0.2, contorno: "#0b0f13" },
  carSuper: { cor: "#f59e0b", forma: "caixa", margem: 0.2, contorno: "#0b0f13" },
  carSuv: { cor: "#16a34a", forma: "caixa", margem: 0.2, contorno: "#0b0f13" },
  streetLamp: { cor: "#fde68a", forma: "redondo", margem: 0.36 },
  parkSign: { cor: "#15803d", forma: "caixa", margem: 0.3 },
  dumpster: { cor: "#3f6212", forma: "caixa", margem: 0.14 },
  trashCan: { cor: "#475569", forma: "redondo", margem: 0.3 },
  cone: { cor: "#f97316", forma: "redondo", margem: 0.36 },
  fence: { cor: "#7a5a36", forma: "vedacao" },
  stoneWall: { cor: "#8a8f96", forma: "vedacao" },
  parkingLines: { cor: "rgba(255, 255, 255, 0.55)", forma: "baias" },
  // 1.º andar
  stairs: { cor: "#9a7148", forma: "escada" },
  stairwell: { cor: "#5a4630", forma: "escada" },
};

/** Ordem de pintura: o que está no chão primeiro, o que está por cima depois. */
const ORDEM_DA_FORMA: Record<EstiloDeMovel["forma"], number> = {
  tapete: 0,
  baias: 0,
  escada: 0,
  vedacao: 1,
  caixa: 2,
  redondo: 3,
};

/** O raio da copa de cada árvore, em tiles. */
const RAIO_DA_COPA: Record<TreeSize, number> = { small: 0.75, medium: 1.05, big: 1.4 };

/** Pinta o mapa inteiro — chão, textura, móveis e árvores. Faz-se UMA vez, num canvas à parte. */
export function pintarFundo(ctx: CanvasRenderingContext2D, px: number): void {
  const linha = Math.max(1, Math.round(px / 16));

  // ── Chão e paredes, com um fio de textura que diz o que é cada chão.
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      const x = col * px;
      const y = row * px;
      ctx.fillStyle = corDoTile(col, row);
      ctx.fillRect(x, y, px, px);

      // O jardim por baixo do 1.º andar pinta-se com a textura do de baixo.
      const espelho =
        isUpstairs(row) && floorAt(col, row) == null && isOutside(col, row - UPSTAIRS_OFFSET_ROWS)
          ? row - UPSTAIRS_OFFSET_ROWS
          : row;
      if (espelho !== row) {
        if (
          !isWall(col, espelho) &&
          !inLake(col, espelho) &&
          !onGardenPath(col, espelho) &&
          (col * 7 + espelho * 3) % 5 === 0
        ) {
          ctx.fillStyle = COR.relvaTufo;
          ctx.fillRect(x + px * 0.3, y + px * 0.4, px * 0.25, px * 0.25);
        }
        continue;
      }

      const chao = floorAt(col, row);
      if (chao === "wood") {
        ctx.fillStyle = "rgba(0, 0, 0, 0.14)"; // as tábuas
        ctx.fillRect(x, y + px / 2, px, linha);
      } else if (chao === "tile" && (col + row) % 2 === 0) {
        ctx.fillStyle = "rgba(255, 255, 255, 0.06)"; // o xadrez dos azulejos
        ctx.fillRect(x, y, px, px);
      } else if (chao === "carpet") {
        ctx.fillStyle = "rgba(255, 255, 255, 0.04)";
        ctx.fillRect(x, y, px, linha);
      } else if (
        !chao &&
        !isWall(col, row) &&
        isOutside(col, row) &&
        !inLake(col, row) &&
        (col * 7 + row * 3) % 5 === 0
      ) {
        ctx.fillStyle = COR.relvaTufo; // tufos na relva
        ctx.fillRect(x + px * 0.3, y + px * 0.4, px * 0.25, px * 0.25);
      }
    }
  }

  // ── O telhado: os painéis solares e as máquinas, por cima da tela.
  for (const peca of PECAS_DO_TELHADO) {
    const medida = MEDIDA_DA_PECA[peca.tipo];
    ctx.fillStyle = peca.tipo === "solar" ? COR.painel : peca.tipo === "ac" ? COR.maquina : COR.heliponto;
    ctx.fillRect(peca.col * px, (peca.row + UPSTAIRS_OFFSET_ROWS) * px, medida.w * px - linha, medida.h * px - linha);
  }

  // ── Móveis, do chão para cima. O que está no jardim pinta-se DUAS vezes: à
  // superfície e na faixa do 1.º andar, porque é o mesmo jardim que se vê de lá.
  const noJardim = (p: (typeof PROPS)[number]) => p.row < SURFACE_ROWS && isOutside(p.col, p.row);
  const moveis = PROPS.flatMap((p) => {
    const e = ESTILO_DOS_MOVEIS[p.kind];
    if (!e) return [];
    return noJardim(p)
      ? [
          { p, e, dy: 0 },
          { p, e, dy: UPSTAIRS_OFFSET_ROWS },
        ]
      : [{ p, e, dy: 0 }];
  }).sort((a, b) => ORDEM_DA_FORMA[a.e.forma] - ORDEM_DA_FORMA[b.e.forma]);

  for (const { p, e, dy } of moveis) {
    const spec = PROP_SPECS[p.kind];
    const x = p.col * px;
    const y = (p.row + dy) * px;
    const w = spec.w * px;
    const h = spec.h * px;
    const m = (e.margem ?? 0) * px;

    if (e.forma === "escada") {
      // Os degraus são as colunas de dentro e as filas de baixo da pegada.
      const degrausW = (spec.w - 1) * px;
      ctx.fillStyle = e.cor;
      ctx.fillRect(x, y + px, degrausW, h - px);
      ctx.fillStyle = "rgba(0, 0, 0, 0.35)";
      for (let i = 1; i < (spec.h - 1) * 2; i++) ctx.fillRect(x, y + px + (i * px) / 2, degrausW, linha);
    } else if (e.forma === "tapete") {
      ctx.globalAlpha = 0.35;
      ctx.fillStyle = e.cor;
      ctx.fillRect(x + m, y + m, w - 2 * m, h - 2 * m);
      ctx.globalAlpha = 1;
    } else if (e.forma === "vedacao") {
      ctx.fillStyle = e.cor;
      ctx.fillRect(x, y + h * 0.38, w, Math.max(linha * 2, h * 0.24));
    } else if (e.forma === "baias") {
      ctx.strokeStyle = e.cor;
      ctx.lineWidth = linha;
      ctx.strokeRect(x + linha, y + linha, w - 2 * linha, h - 2 * linha);
      for (let c = 2; c < spec.w; c += 2) {
        ctx.beginPath();
        ctx.moveTo(x + c * px, y);
        ctx.lineTo(x + c * px, y + h);
        ctx.stroke();
      }
    } else if (e.forma === "redondo") {
      ctx.beginPath();
      ctx.ellipse(x + w / 2, y + h / 2, Math.max(1, w / 2 - m), Math.max(1, h / 2 - m), 0, 0, Math.PI * 2);
      ctx.fillStyle = e.cor;
      ctx.fill();
      if (e.contorno) {
        ctx.lineWidth = linha;
        ctx.strokeStyle = e.contorno;
        ctx.stroke();
      }
    } else {
      ctx.fillStyle = e.cor;
      ctx.fillRect(x + m, y + m, w - 2 * m, h - 2 * m);
      if (e.contorno) {
        ctx.lineWidth = linha;
        ctx.strokeStyle = e.contorno;
        ctx.strokeRect(x + m + linha / 2, y + m + linha / 2, w - 2 * m - linha, h - 2 * m - linha);
      }
    }
  }

  // ── Árvores: a copa vista de cima, com um lado mais claro. Também duas vezes.
  for (const [arvore, dy] of TREES.flatMap((t) => [[t, 0] as const, [t, UPSTAIRS_OFFSET_ROWS] as const])) {
    const { col, row } = trunkTile(arvore);
    const r = RAIO_DA_COPA[arvore.size] * px;
    const cx = (col + 0.5) * px;
    const cy = (row + dy + 0.1) * px;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = COR.copa;
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx - r * 0.25, cy - r * 0.25, r * 0.55, 0, Math.PI * 2);
    ctx.fillStyle = COR.copaLuz;
    ctx.fill();
  }
}

// ── Pontos de interesse ────────────────────────────────────────────────────

export type TipoDePonto = "ponto" | "quadro" | "jogo" | "arcade" | "tv" | "escotilha";

export interface PontoDeInteresse {
  tipo: TipoDePonto;
  nome: string;
  x: number;
  y: number;
  /** Só nas escotilhas: para onde se vai. */
  seta?: "baixo" | "cima";
}

const PONTO_DO_MOVEL: Partial<Record<PropKind, { tipo: TipoDePonto; nome: string }>> = {
  grandfatherClock: { tipo: "ponto", nome: "Bater ponto" },
  whiteboard: { tipo: "quadro", nome: "Quadro branco" },
  gameTable: { tipo: "jogo", nome: "4 em Linha" },
  arcade: { tipo: "arcade", nome: "Green Rush" },
  tv: { tipo: "tv", nome: "TV" },
};

/** O que se pode USAR no mapa, com um ícone: o relógio do ponto, o quadro, os jogos, a TV, as escotilhas. */
export const PONTOS_DE_INTERESSE: readonly PontoDeInteresse[] = [
  ...PROPS.flatMap((p): PontoDeInteresse[] => {
    const ponto = PONTO_DO_MOVEL[p.kind];
    if (!ponto) return [];
    const spec = PROP_SPECS[p.kind];
    return [{ ...ponto, x: (p.col + spec.w / 2) * TILE, y: (p.row + spec.h / 2) * TILE }];
  }),
  ...TELEPORTS.map((t): PontoDeInteresse => {
    const c = teleportCenter(t);
    return {
      tipo: "escotilha",
      nome: t.label,
      x: c.x,
      y: c.y,
      seta: isUnderground(Math.floor(c.y / TILE)) ? "cima" : "baixo",
    };
  }),
  // As escadas, uma marca em cada andar, no meio dos degraus.
  ...ESCADAS.flatMap((e): PontoDeInteresse[] => {
    const x = (e.col + e.w / 2) * TILE;
    const y = ((e.topo + e.fundo + 1) / 2) * TILE;
    return [
      { tipo: "escotilha", nome: "Escada para o 1.º andar", x, y, seta: "cima" },
      { tipo: "escotilha", nome: "Escada para a Recepção", x, y: y + UPSTAIRS_OFFSET_ROWS * TILE, seta: "baixo" },
    ];
  }),
];

// ── Superfície, bunker e 1.º andar ─────────────────────────────────────────

/**
 * A superfície, o bunker e o 1.º andar são três faixas do mesmo mapa. Quem está
 * numa não vê as outras — nem o chão, nem quem lá anda — que é a regra da câmara.
 */
export type Banda = "superficie" | "subsolo" | "andar1";

export function bandaDe(y: number): Banda {
  const row = Math.floor(y / TILE);
  if (isUnderground(row)) return "subsolo";
  return isUpstairs(row) ? "andar1" : "superficie";
}

/** O intervalo de Y (em px do mundo) de uma banda. */
export function limitesDaBanda(banda: Banda): { y0: number; y1: number } {
  if (banda === "subsolo") return { y0: SURFACE_ROWS * TILE, y1: UPSTAIRS_TOP_ROW * TILE };
  if (banda === "andar1") return { y0: UPSTAIRS_TOP_ROW * TILE, y1: MAP_HEIGHT };
  return { y0: 0, y1: SURFACE_ROWS * TILE };
}

/** As pessoas que aparecem no mapa de quem está em `selfY`: as outras, na mesma banda. */
export function pessoasVisiveis(
  ocupantes: readonly SpaceOccupant[],
  selfId: string | null,
  selfY: number,
): SpaceOccupant[] {
  const banda = bandaDe(selfY);
  return ocupantes.filter((o) => o.userId !== selfId && bandaDe(o.y) === banda);
}

/**
 * Aproxima as posições desenhadas das que chegaram do servidor, como o mundo faz
 * com os bonecos (`reconcileRemotes`). O servidor manda uma posição a cada
 * ~100 ms; desenhada tal e qual, a bolinha andava aos saltos no minimapa.
 *
 * `peso` é a fracção do caminho por frame de 60fps; aqui escala-se pelo tempo
 * que passou, porque o minimapa não desenha a 60fps certos. Um salto maior do
 * que `salto` (escada, teletransporte) não se anima: a bolinha aparece lá.
 * Altera `mostradas` no sítio e esquece quem já não está.
 */
export function aproximarPosicoes(
  mostradas: Map<string, { x: number; y: number }>,
  alvos: readonly { userId: string; x: number; y: number }[],
  dtMs: number,
  peso: number,
  salto: number,
): void {
  const k = 1 - (1 - peso) ** (Math.max(0, dtMs) / (1000 / 60));
  const vistos = new Set<string>();
  for (const alvo of alvos) {
    vistos.add(alvo.userId);
    const p = mostradas.get(alvo.userId);
    if (!p || Math.hypot(alvo.x - p.x, alvo.y - p.y) > salto) {
      mostradas.set(alvo.userId, { x: alvo.x, y: alvo.y });
      continue;
    }
    p.x += (alvo.x - p.x) * k;
    p.y += (alvo.y - p.y) * k;
  }
  for (const id of mostradas.keys()) if (!vistos.has(id)) mostradas.delete(id);
}

// ── Informação ─────────────────────────────────────────────────────────────

/** Quantas pessoas há em cada sala (pelo id da sala). Quem está no corredor ou lá fora não conta. */
export function pessoasPorSala(pontos: readonly { x: number; y: number }[]): Map<string, number> {
  const contagem = new Map<string, number>();
  for (const p of pontos) {
    const sala = roomAt(p.x, p.y);
    if (sala) contagem.set(sala.id, (contagem.get(sala.id) ?? 0) + 1);
  }
  return contagem;
}

/** As salas trancadas neste momento, a partir do mapa de trancas da store. */
export function salasTrancadas(trancas: Readonly<Record<string, string>> | undefined): Room[] {
  if (!trancas) return [];
  return ROOMS.filter((r) => trancas[r.id] !== undefined);
}

/** `15:07` — a hora do relógio por baixo do disco. */
export function horaDoRelogio(d: Date): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

// ── Navegação e desenho ────────────────────────────────────────────────────

/**
 * O ponto andável mais perto de onde se carregou no mapa, até 4 tiles à volta.
 *
 * Carregar em cima de uma mesa ou de uma parede é o gesto normal — ninguém mira
 * o chão entre dois móveis num mapa pequeno. Nunca salta de banda: carregar na
 * orla da superfície não manda ninguém para o bunker.
 */
export function destinoMaisProximo(px: number, py: number): { x: number; y: number } | null {
  const c0 = Math.floor(px / TILE);
  const r0 = Math.floor(py / TILE);
  if (c0 < 0 || r0 < 0 || c0 >= COLS || r0 >= ROWS) return null;
  const banda = bandaDe((r0 + 0.5) * TILE);

  for (let raio = 0; raio <= 4; raio++) {
    let melhor: { x: number; y: number } | null = null;
    let melhorDistancia = Number.POSITIVE_INFINITY;
    for (let dr = -raio; dr <= raio; dr++) {
      for (let dc = -raio; dc <= raio; dc++) {
        if (Math.max(Math.abs(dr), Math.abs(dc)) !== raio) continue;
        const row = r0 + dr;
        if (bandaDe((row + 0.5) * TILE) !== banda) continue;
        const x = (c0 + dc + 0.5) * TILE;
        const y = (row + 0.5) * TILE;
        if (!isWalkable(x, y)) continue;
        const d = Math.hypot(x - px, y - py);
        if (d < melhorDistancia) {
          melhor = { x, y };
          melhorDistancia = d;
        }
      }
    }
    if (melhor) return melhor;
  }
  return null;
}

/**
 * Um ponto fora do minimapa fica preso à borda, na direcção certa — como no GTA,
 * sabe-se para que lado está quem já não cabe no círculo.
 */
export function naBorda(dx: number, dy: number, raio: number): { dx: number; dy: number; fora: boolean } {
  const d = Math.hypot(dx, dy);
  if (d <= raio) return { dx, dy, fora: false };
  return { dx: (dx / d) * raio, dy: (dy / d) * raio, fora: true };
}

/** O ângulo (radianos, 0 = direita) da seta de quem está virado para `facing`. */
export function anguloDoRumo(facing: Facing): number {
  if (facing === "up") return -Math.PI / 2;
  if (facing === "down") return Math.PI / 2;
  if (facing === "left") return Math.PI;
  return 0;
}
